import { useState, useEffect } from "react";
import moment from "moment";

import { Card, Button, Input, Select, Typography, Form, Modal } from "antd";
import { UploadOutlined } from "@ant-design/icons";

import notifier from "utils/notifier";
import { ErrorResponse } from "utils/types";
import {
  sortArrayByKey,
  getDisplayCompanyId,
  validatePhoneNumber,
  phoneFormatInput,
  validateLetterOnly,
  validateZipCode,
  validateNumberExtension,
} from "utils/helpers";
import { states } from "utils/objects";

import { GetAuthEnabledProviderListResponse } from "store/authorization/provider/actions";

import FloatLabel from "components/Common/FloatLabel";
import RequestedMemberList from "components/Eligibility/MemberRequestForm/RequestedMembersList";
import DatePickerLocalised from "components/Common/DatePickerLocalised";

import MemberRequestFormProps from "./Proptypes";

import styles from "./MemberRequestForm.module.scss";

const { Title } = Typography;
const { Option } = Select;
const { TextArea } = Input;

const MemberRequestForm = (props: MemberRequestFormProps) => {
  const [providerList, setProviderList] = useState([] as Object[]);
  const [ipaList, setIpaList] = useState([] as Object[]);
  const [companyId, setCompanyId] = useState("");
  const [birthDate, setBirthDate] = useState("");
  const [provider, setProvider] = useState("");

  const [note, setNote] = useState("");
  const [pageState, setPageState] = useState("start");
  const [form] = Form.useForm();

  const requestedMembers = () => {
    setPageState("view-requests");
  };

  const pageStartHandler = () => {
    setPageState("start");
  };

  const parse = (ipa: any) => {
    return JSON.parse(ipa || {});
  };

  const submitForm = () => {
    form.validateFields().then((values: any) => {
      const formData = {
        Address: values.address,
        City: values.city,
        State: values.state,
        Zip: values.zipCode,
        CompanyId: parse(values.companyId)?.RmiCompanyId,
        Contact: values.contactPerson,
        ContactEmail: values.email,
        ContactPhone: values.contactPhoneNumber?.replace(/[^A-Z0-9]/gi, ""),
        DateOfBirth: birthDate === "" ? null : birthDate,
        FirstName: values.firstName,
        Gender: values.gender,
        HPMemberId: values.healthPlanMemberId,
        HealthPlan: values.healthPlan,
        LastName: values.lastName,
        Note: note,
        Phone: values.phoneNumber?.replace(/[^A-Z0-9]/gi, ""),
        Provider: parse(provider),
        ProviderName: parse(provider)?.RmiFullName,
        UserId: sessionStorage.user_id,
      };

      return props
        .requestMember(formData, sessionStorage.user_id)
        .then((response: any) => {
          if (response.success) {
            Modal.success({
              title: "Success",
              content: response.message,
            });
          } else {
            Modal.error({
              title: "Error",
              content: (
                <>
                  <p>
                    <strong>Original Health Plan:</strong> {values.healthPlan}
                  </p>
                  <p>
                    <strong>Original Health Plan Member ID:</strong>{" "}
                    {values.healthPlanMemberId}
                  </p>
                  {response.message}
                </>
              ),
            });
          }
        })
        .catch((err: ErrorResponse) => {
          notifier.error(
            err.response?.data.Message ||
              "Something went wrong, please contact a system administrator."
          );
        });
    });
  };

  const getAuthEnabledProviderList = () => {
    const authEnabledProviderListParam = {
      UserId: sessionStorage.user_id,
      UserRole: sessionStorage.user_role,
    };

    return props
      .getAuthEnabledProviderList(authEnabledProviderListParam)
      .then((response: GetAuthEnabledProviderListResponse[]) => {
        const providers = response.filter(
          (provider) =>
            provider.RmiClaimAccessDays === null ||
            parseInt(provider.RmiClaimAccessDays) > 0
        );

        setProvider(JSON.stringify(providers[0])); //legacy code gets the first array after checking the RmiClaimAccessDays as the default
        setProviderList(sortArrayByKey(response, "Provider"));
      })
      .catch((err: ErrorResponse) => {
        notifier.error(
          err.response?.data.Message ||
            "Something went wrong, please contact a system administrator."
        );
      });
  };

  const getAllDesc = () => {
    return props
      .getAllDesc()
      .then((response: any) => {
        const ipaList = sortArrayByKey(response, "RmiCompanyId");
        setCompanyId(JSON.stringify(ipaList[0]));
        setIpaList(ipaList);
      })
      .catch((err: ErrorResponse) => {
        notifier.error(
          err.response?.data?.Message ||
            "Something went wrong, please contact a system administrator."
        );
      });
  };

  useEffect(() => {
    getAuthEnabledProviderList();
    getAllDesc();
  }, []);

  return (
    <div>
      {pageState === "start" && (
        <Form form={form} name="member-request-form" onFinish={submitForm}>
          <div
            className={`${styles["member-request-form"]} row flex-lg-wrap justify-content-start mb-3 ml-0 mt-3`}
          >
            <div className="row" style={{ width: "100%" }}>
              <div className="col-lg-22 col-md-22">
                <Title level={3}>Request for a New Member</Title>
              </div>
              <div className="col-lg-2 col-md-2">
                <Button
                  className={`${styles["member-request-form-btn"]} ${styles["view-request-btn"]} ${styles["float-right"]}`}
                  onClick={requestedMembers}
                >
                  View Requests
                </Button>
              </div>
            </div>
            <div className="member-search row flex-lg-wrap justify-content-start mb-4 ml-0">
              <Card title="Member" style={{ width: "100%" }}>
                <div className="row">
                  <div className="col-lg-8 col-md-4">
                    <FloatLabel
                      name="companyId"
                      label="Medical Group / IPA: "
                      form={form}
                      required
                    >
                      <Select
                        value={companyId}
                        onChange={(value) => setCompanyId(value)}
                      >
                        {ipaList.map((option: any, index: number) => {
                          return (
                            <Option key={index} value={JSON.stringify(option)}>
                              {getDisplayCompanyId(option.RmiCompanyId)}
                            </Option>
                          );
                        })}
                      </Select>
                    </FloatLabel>
                  </div>
                  <div className="col-lg-8 col-md-4">
                    <FloatLabel
                      name="healthPlan"
                      label="Health Plan: "
                      form={form}
                      validateTrigger={["onSubmit"]}
                      required
                    >
                      <Input />
                    </FloatLabel>
                  </div>
                  <div className="col-lg-8 col-md-4">
                    <FloatLabel
                      name="healthPlanMemberId"
                      label="Health Plan Member ID: "
                      form={form}
                      validateTrigger={["onSubmit"]}
                      rules={[
                        () => ({
                          async validator(_: any, value: any) {
                            const numbersOnly = new RegExp("[^A-Za-z0-9]+");
                            if (numbersOnly.test(value)) {
                              return Promise.reject(
                                "Please enter letters and numbers only."
                              );
                            }
                            return Promise.resolve();
                          },
                        }),
                      ]}
                      required
                    >
                      <Input />
                    </FloatLabel>
                  </div>
                </div>
                <div className="row">
                  <div className="col-lg-12 col-md-4">
                    <FloatLabel
                      name="firstName"
                      label="Member First Name: "
                      form={form}
                      validateTrigger={["onSubmit"]}
                      required
                    >
                      <Input />
                    </FloatLabel>
                  </div>
                  <div className="col-lg-12 col-md-4">
                    <FloatLabel
                      name="lastName"
                      label="Member Last Name: "
                      form={form}
                      validateTrigger={["onSubmit"]}
                      required
                    >
                      <Input />
                    </FloatLabel>
                  </div>
                </div>
                <div className="row">
                  <div className="col-lg-8 col-md-4">
                    <FloatLabel
                      name="birthDate"
                      label="Date of Birth (MM/DD/YYYY): "
                      form={form}
                      validateTrigger={["onSubmit"]}
                      rules={[
                        () => ({
                          async validator() {
                            const value = form.getFieldValue("birthDate");
                            const validBirthDate = () =>
                              (!value || (moment(value).isValid() && moment(value).year() >= 1900)) &&
                              (!value || value.match(/^\d{2}\/\d{2}\/\d{4}$/));
                            if (validBirthDate()) {
                              return Promise.resolve();
                            }

                            return Promise.reject(new Error("Invalid Date"));
                          },
                        }),
                      ]}
                      required
                    >
                      <DatePickerLocalised
                        date={birthDate}
                        setDate={setBirthDate}
                        formInstance={form}
                        formItemName={"birthDate"}
                      />
                    </FloatLabel>
                  </div>
                  <div className="col-lg-8 col-md-4">
                    <FloatLabel
                      name="gender"
                      label="Gender: "
                      initialValue={"M"}
                      form={form}
                      validateTrigger={["onSubmit"]}
                      required
                    >
                      <Select
                        value={form.getFieldValue("gender")}
                        onChange={(value) =>
                          form.setFieldsValue({
                            gender: value,
                          })
                        }
                      >
                        <Option value="M">Male</Option>
                        <Option value="F">Female</Option>
                      </Select>
                    </FloatLabel>
                  </div>
                  <div className="col-lg-8 col-md-4">
                    <FloatLabel
                      label="Phone Number"
                      name="phoneNumber"
                      form={form}
                      validateTrigger={["onSubmit"]}
                      rules={[
                        () => ({
                          validator: validatePhoneNumber,
                        }),
                      ]}
                    >
                      <Input
                        type="tel"
                        onKeyPress={(e: any) =>
                          form.setFieldsValue({
                            phoneNumber: phoneFormatInput(e),
                          })
                        }
                      />
                    </FloatLabel>
                  </div>
                </div>
                <div className="row">
                  <div className="col-lg-24 col-md-4">
                    <FloatLabel
                      label="Address"
                      name="address"
                      form={form}
                      validateTrigger={["onSubmit"]}
                    >
                      <Input
                        onKeyPress={(e: any) =>
                          form.setFieldsValue({
                            address: e.target.value,
                          })
                        }
                      />
                    </FloatLabel>
                  </div>
                </div>
                <div className="row">
                  <div className="col-lg-12 col-md-4">
                    <FloatLabel
                      label="City"
                      name="city"
                      form={form}
                      validateTrigger={["onSubmit"]}
                    >
                      <Input
                        onKeyPress={(e: any) =>
                          form.setFieldsValue({
                            city: e.target.value,
                          })
                        }
                      />
                    </FloatLabel>
                  </div>
                  <div className="col-lg-6 col-md-4">
                    <FloatLabel
                      label="State"
                      name="state"
                      form={form}
                      validateTrigger={["onSubmit"]}
                    >
                      <Select
                        value={form.getFieldValue("state")}
                        showSearch
                        onSelect={(value: any) =>
                          form.setFieldsValue({
                            state: value,
                          })
                        }
                      >
                        {states.map((state: any) => (
                          <Option key={state} value={state}>
                            {state}
                          </Option>
                        ))}
                      </Select>
                    </FloatLabel>
                  </div>
                  <div className="col-lg-6 col-md-4">
                    <FloatLabel
                      label="Zipcode"
                      name="zipCode"
                      form={form}
                      validateTrigger={["onSubmit"]}
                      rules={[
                        () => ({
                          validator: () =>
                            validateZipCode(form.getFieldValue("zipCode")),
                        }),
                      ]}
                    >
                      <Input
                        onKeyPress={(e: any) =>
                          form.setFieldsValue({
                            zipCode: e.target.value,
                          })
                        }
                        maxLength={5}
                      />
                    </FloatLabel>
                  </div>
                </div>
              </Card>
            </div>
            <div className="member-search row flex-lg-wrap justify-content-start mb-4 ml-0">
              <Card title="Contact Information" style={{ width: "100%" }}>
                <div className="row">
                  <div className="col-lg-12 col-md-4">
                    <FloatLabel
                      label="Provider"
                      value={provider}
                      validateTrigger={["onSubmit"]}
                    >
                      <Select
                        value={provider}
                        onChange={(value) => setProvider(value)}
                      >
                        {providerList.map((option: any, index: number) => {
                          return (
                            <Option key={index} value={JSON.stringify(option)}>
                              {option.Provider.replace("CVIPA", "EHIPA")}
                            </Option>
                          );
                        })}
                      </Select>
                    </FloatLabel>
                  </div>
                  <div className="col-lg-12 col-md-4">
                    <FloatLabel
                      name="contactPerson"
                      label="Contact Person: "
                      form={form}
                      validateTrigger={["onSubmit"]}
                      required
                    >
                      <Input />
                    </FloatLabel>
                  </div>
                </div>
                <div className="row">
                  <div className="col-lg-12 col-md-4">
                    <FloatLabel
                      name="email"
                      label="Email: "
                      form={form}
                      validateTrigger={["onSubmit"]}
                      rules={[
                        {
                          required: true,
                          type: "email",
                          message: "Please enter a valid email address",
                        },
                      ]}
                      star
                    >
                      <Input />
                    </FloatLabel>
                  </div>
                  <div className="col-lg-10 col-md-4">
                    <FloatLabel
                      name="contactPhoneNumber"
                      label="Phone Number: "
                      form={form}
                      validateTrigger={["onSubmit"]}
                      rules={[
                        () => ({
                          validator: validatePhoneNumber,
                        }),
                      ]}
                      required
                    >
                      <Input
                        type="tel"
                        onKeyPress={(e: any) =>
                          form.setFieldsValue({
                            contactPhoneNumber: phoneFormatInput(e),
                          })
                        }
                      />
                    </FloatLabel>
                  </div>
                  <div className="col-lg-2 col-md-4">
                    <FloatLabel
                      name="contactPhoneNumberExt"
                      label="Ext"
                      form={form}
                      validateTrigger={["onSubmit"]}
                      rules={[
                        () => ({
                          validator: () =>
                            validateNumberExtension(
                              form.getFieldValue("contactPhoneNumberExt")
                            ),
                        }),
                      ]}
                    >
                      <Input type="tel" maxLength={5} />
                    </FloatLabel>
                  </div>
                </div>
                <div className="row">
                  <div className="col-lg-24 col-md-4">
                    <FloatLabel label="Notes" value={note}>
                      <TextArea
                        rows={4}
                        value={note}
                        onChange={(e) => setNote(e.target.value)}
                      />
                    </FloatLabel>
                  </div>
                </div>
              </Card>
            </div>

            <div className="col-lg-11 col-md-11 mt-2">Required Fields *</div>
            <div className="col-lg-13 col-md-13 mt-2">
              <div className="btn-wrapper">
                <Button
                  className={`${styles["member-request-form-btn"]} ${styles["submit-btn"]}`}
                  onClick={submitForm}
                  loading={props.isRequestingMember}
                  icon={<UploadOutlined />}
                >
                  Submit
                </Button>
              </div>
            </div>
          </div>
        </Form>
      )}
      {pageState === "view-requests" && (
        <RequestedMemberList
          pageStartHandler={pageStartHandler}
          getMemberRequest={props.getMemberRequest}
          search={props.search}
          isFetchingMember={props.isFetchingMember}
        />
      )}
    </div>
  );
};

export default MemberRequestForm;
